import { css } from '@linaria/core'

import { notCss } from './util'

/**
 * Specific `font-size` values to be directly referenced:
 * @example const myStyleClass = css`font-size: ${style.fontSize.m}`
 */
export const fontSizeValues = notCss({
  xxs: '10px',
  xs: '11px',
  s: '12px',
  m: '14px',
  l: '20px',
  xl: '28px',
  xxl: '36px',
} as const)

/**
 * Specific `line-height` values to be directly referenced:
 * @example const myStyleClass = css`line-height: ${style.lineHeight.m}`
 */
export const lineHeightValues = notCss({
  xxs: '120%',
  xs: '110%',
  s: '110%',
  m: '110%',
  l: '100%',
  xl: '100%',
  xxl: '100%',
} as const)
export const fontXXS = ` font-size: ${fontSizeValues.xxs}; line-height: ${lineHeightValues.xxs}; ` as const
export const fontXS = ` font-size: ${fontSizeValues.xs}; line-height: ${lineHeightValues.xs}; ` as const
export const fontS = ` font-size: ${fontSizeValues.s}; line-height: ${lineHeightValues.s}; ` as const
export const fontM = ` font-size: ${fontSizeValues.m}; line-height: ${lineHeightValues.m}; ` as const
export const fontL = ` font-size: ${fontSizeValues.l}; line-height: ${lineHeightValues.l}; ` as const
export const fontXL = ` font-size: ${fontSizeValues.xl}; line-height: ${lineHeightValues.xl}; ` as const
export const fontXXL = ` font-size: ${fontSizeValues.xxl}; line-height: ${lineHeightValues.xxl}; ` as const

export const fontSizeAtomics = notCss({
  xxs: css`
    ${fontXXS}
  `,
  xs: css`
    ${fontXS}
  `,
  s: css`
    ${fontS}
  `,
  l: css`
    ${fontL}
  `,
  xl: css`
    ${fontXL}
  `,
  xxl: css`
    ${fontXXL}
  `,
  default: css`
    ${fontM}
  `,
  regular: css`
    ${fontM}
  `,
} as const)

/**
 * CSS Fragments to be used, to cut down on repeatedly writing the same definitions
 * @example const myStyleClass = css`
 *   ${fragments.fontSize.xxl}
 *   font-weight: ${style.fontWeight.regular};
 * `
 */
export const fontSizeFragments = notCss({
  xxs: fontXXS,
  xs: fontXS,
  s: fontS,
  m: fontM,
  l: fontL,
  xl: fontXL,
  xxl: fontXXL,
  default: fontM,
  regular: fontM,
} as const)

/**
 * Specific `font-weight` values to be directly referenced:
 * @example const myStyleClass = css`font-weight: ${style.fontWeight.regular}`
 */
export const fontWeightValues = notCss({
  thin: 100,
  light: 200,
  regular: 'normal',
  medium: 600,
  bold: 700,
} as const)
export const fontWeightThin = ` font-weight: ${fontWeightValues.thin}; ` as const
export const fontWeightLight = ` font-weight: ${fontWeightValues.light}; ` as const
export const fontWeightRegular = ` font-weight: ${fontWeightValues.regular}; ` as const
export const fontWeightMedium = ` font-weight: ${fontWeightValues.medium}; ` as const
export const fontWeightBold = ` font-weight: ${fontWeightValues.bold}; ` as const

/**
 * Specific font weight fragments to be used in css
 * @example const myStyleClass = css`
 *  ${fragments.fontWeight.bold}
 *  ${fragments.fontSize.xxl}
 * `
 */
export const fontWeightFragments = notCss({
  thin: fontWeightThin,
  light: fontWeightLight,
  regular: fontWeightRegular,
  normal: fontWeightRegular,
  medium: fontWeightMedium,
  bold: fontWeightBold,
} as const)
/**
 * Specific `font-weight` atomic classes to be used in components
 * @example <div className={cx(atomics.fontWeight.light, atomics.fontStretch.condensed)} />
 */
export const fontWeightAtomics = notCss({
  light: css`
    ${fontWeightLight}
  `,
  regular: css`
    ${fontWeightRegular}
  `,
  normal: css`
    ${fontWeightRegular}
  `,
  bold: css`
    ${fontWeightBold}
  `,
} as const)

/**
 * Specific `font-stretch` values to be directly referenced
 * @example const myStyleClass = css`font-stretch: ${style.fontStretch.regular}`
 */
export const fontStretchValues = notCss({
  condensed: 'condensed',
  normal: 'normal',
  expanded: '125%',
} as const)
export const fontStretchCondensed = ` font-stretch: ${fontStretchValues.condensed}; ` as const
export const fontStretchNormal = ` font-stretch: ${fontStretchValues.normal}; ` as const
export const fontStretchExpanded = ` font-stretch: ${fontStretchValues.expanded}; ` as const

/**
 * Specific `font-stretch` fragments to be used in css
 * @example const myStyleClass = css`
 *    ${fragments.fontStretch.expanded};
 *    ${fragments.fontSize.xxl};
 * `
 */
export const fontStretchFragments = notCss({
  condensed: fontStretchCondensed,
  normal: fontStretchNormal,
  expanded: fontStretchExpanded,
} as const)

/**
 * Specific `font-stretch` atomics to be used in components
 * @example <div className={cx(atomics.fontStretch.condensed, atomics.fontWeight.light)} />
 */
export const fontStretchAtomics = notCss({
  condensed: css`
    ${fontStretchCondensed}
  `,
  normal: css`
    ${fontStretchNormal}
  `,
  expanded: css`
    ${fontStretchExpanded}
  `,
} as const)

// additionally some combinatory styles
export const fontStyleLightCondensed = `${fontWeightLight}${fontStretchCondensed}` as const
export const fontStyleLightNormal = `${fontWeightLight}${fontStretchNormal}` as const
export const fontStyleLightExpanded = `${fontWeightLight}${fontStretchExpanded}` as const
export const fontStyleRegularCondensed = `${fontWeightRegular}${fontStretchCondensed}` as const
export const fontStyleRegularNormal = `${fontWeightRegular}${fontStretchNormal}` as const
export const fontStyleRegularExpanded = `${fontWeightRegular}${fontStretchExpanded}` as const
export const fontStyleBoldCondensed = `${fontWeightBold}${fontStretchCondensed}` as const
export const fontStyleBoldNormal = `${fontWeightBold}${fontStretchNormal}` as const
export const fontStyleBoldExpanded = `${fontWeightBold}${fontStretchExpanded}` as const

/**
 * Combinatory font style fragments to be used in css
 * @example const myStyleClass = css`
 *   ${fragments.fontStyle.boldExpanded};
 *   ${fragments.fontSize.xxl};
 * `
 */
export const fontStyleFragments = notCss({
  lightCondensed: fontStyleLightCondensed,
  lightNormal: fontStyleLightNormal,
  lightExpanded: fontStyleLightExpanded,
  regularCondensed: fontStyleRegularCondensed,
  regularNormal: fontStyleRegularNormal,
  regularExpanded: fontStyleRegularExpanded,
  boldCondensed: fontStyleBoldCondensed,
  boldNormal: fontStyleBoldNormal,
  boldExpanded: fontStyleBoldExpanded,
} as const)

/**
 * Combinatory font style properties to be used in components
 * @example <div className={cx(atomics.fontStyles.boldExpanded, atomics.fontSize.xxl)} />
 */
export const fontStyleAtomics = notCss({
  lightCondensed: css`
    ${fontStyleLightCondensed}
  `,
  lightNormal: css`
    ${fontStyleLightNormal}
  `,
  lightExpanded: css`
    ${fontStyleLightExpanded}
  `,
  regularCondensed: css`
    ${fontStyleRegularCondensed}
  `,
  regularNormal: css`
    ${fontStyleRegularNormal}
  `,
  regularExpanded: css`
    ${fontStyleRegularExpanded}
  `,
  boldCondensed: css`
    ${fontStyleBoldCondensed}
  `,
  boldNormal: css`
    ${fontStyleBoldNormal}
  `,
  boldExpanded: css`
    ${fontStyleBoldExpanded}
  `,
})
